"use client";

import { useState, useEffect, useRef } from "react";
import vilvaLogo from "@/public/images/assets/vilva-logo.svg";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";
import axios from "axios";
import toast from "react-hot-toast";
import { NavbarArrow as Icon_NavbarArrow } from "@/components/customer/icons/NavbarArrow";
import { logout as Icon_logout } from "@/components/customer/icons/logout";
const SvgIcons = { NavbarArrow: Icon_NavbarArrow, logout: Icon_logout };
import { getImageUrl } from "@/lib/imageUrl";
import { useUser } from "./userContext";
import { getApiUrl } from "@/lib/apiUrl";
export default function Navbar() {
  const { user, isLoggedIn, logout } = useUser();
  const data = {
    name: user?.username || "",
    email: user?.email || "",
    id: user?.id || "",
    profile_pic: user?.profile_pic || null,
  };
  const [key] = useState("waSs57FdgthqfzVkv3RMrpTjBcYGKJJRQWtNxhmbS2EPpUjwy8");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState<{ [key: string]: boolean }>({});
  const pathname = usePathname();
  const router = useRouter();
  const timeoutRef = useRef<{ [key: string]: NodeJS.Timeout | null }>({});
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const encryptData = async (plainText: string) => {
    const encoder = new TextEncoder();
    const encodedKey = encoder.encode(key);
    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      await crypto.subtle.digest("SHA-256", encodedKey),
      { name: "AES-GCM" },
      false,
      ["encrypt"]
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      cryptoKey,
      encoder.encode(plainText)
    );
    return btoa(String.fromCharCode(...[...iv, ...new Uint8Array(ciphertext)]));
  };

  const decryptData = async (encryptedData: string) => {
    const decoder = new TextDecoder();
    const data = Uint8Array.from(atob(encryptedData), (char) => char.charCodeAt(0));
    const iv = data.slice(0, 12);
    const ciphertext = data.slice(12);
    const encodedKey = new TextEncoder().encode(key);
    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      await crypto.subtle.digest("SHA-256", encodedKey),
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      cryptoKey,
      ciphertext
    );
    return decoder.decode(decrypted);
  };

  const toggleSubMenu = (menu: string) => {
    setIsSubMenuOpen((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const handleMouseEnter = (menu: string, parentMenu?: string) => {
    if (timeoutRef.current[menu]) {
      clearTimeout(timeoutRef.current[menu]);
      timeoutRef.current[menu] = null;
    }

    setIsSubMenuOpen((prev) => {
      const newState = { ...prev };

      if (parentMenu) {
        Object.keys(prev).forEach((key) => {
          if (key.startsWith(parentMenu) && key !== menu && key !== parentMenu) {
            newState[key] = false;
          }
        });
      } else {
        navItems.forEach((item) => {
          if (item.name !== menu && item.subItems) {
            newState[item.name] = false;
            const closeSubItems = (subItems: any[], parentKey: string) => {
              subItems.forEach((subItem) => {
                const subKey = `${parentKey}.${subItem.name}`;
                newState[subKey] = false;
                if (subItem.subItems) {
                  closeSubItems(subItem.subItems, subKey);
                }
              });
            };
            closeSubItems(item.subItems, item.name);
          }
        });
      }

      newState[menu] = true;
      return newState;
    });
  };

  const handleMouseLeave = (menu: string) => {
    timeoutRef.current[menu] = setTimeout(() => {
      setIsSubMenuOpen((prev) => (prev[menu] ? { ...prev, [menu]: false } : prev));
    }, 200);
  };

  const handleProfileHover = () => {
    setIsProfileOpen(true);
  };

  const handleDropdownLeave = () => {
    setIsProfileOpen(false);
  };

  const handleLogout = async () => {
    setLoader(true);
    const token = localStorage.getItem("authToken");
    try {
      await axios.post(
        getApiUrl("/logout"),
        {},
        {
          headers: {
            'Clear-Site-Data': '"cache", "cookies", "storage"',
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      logout();
      localStorage.removeItem("authToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("user_first_name");
      localStorage.removeItem("user_last_name");
      localStorage.removeItem("user_email");
      localStorage.removeItem("user_mobile");
      localStorage.removeItem("user_business_name");

      toast.success("Logged out successfully", {
        style: {
          border: "1px solid #00b852",
          padding: "16px",
          color: "#00b852",
          zIndex: "10000",
        },
        iconTheme: {
          primary: "#00b852",
          secondary: "#FFFAEE",
        },
      });

      router.push("/signin");
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        logout();
        localStorage.removeItem("authToken");
        localStorage.removeItem("userId");
        localStorage.removeItem("user_first_name");
        localStorage.removeItem("user_last_name");
        localStorage.removeItem("user_email");
        localStorage.removeItem("user_mobile");
        localStorage.removeItem("user_business_name");
        router.push("/signin");
      } else {
        console.error("Error during logout:", error.message);
        toast.error(error.message || "Logout failed", {
          style: {
            border: "1px solid #cc0000",
            padding: "16px",
            color: "#cc0000",
            zIndex: "10000",
          },
          iconTheme: {
            primary: "#cc0000",
            secondary: "#FFFAEE",
          },
        });
      }
    } finally {
      setLoader(false);
    }
  };

  const navItems = [
    {
      name: "Products",
      subItems: [
        { name: "Business Websites", href: "/business-websites", ariaLabel: "Explore Growsuite product" },
        { name: "Vilva Chat - Official Business WhatsApp API", href: "/vilvachat-official-whatsapp-business-api", ariaLabel: "Explore Growsuite product" },
        { name: "Growsuite (CRM and HRM)", href: "/growsuite", ariaLabel: "Explore Growsuite product" },
        { name: "Vilva D2C", href: "/d2c-e-commerce-platform-for-growth-stage-businesses", ariaLabel: "Learn about Vilva D2C solution" },
        // { name: "Vilva One", href: "/vilva-one", ariaLabel: "Discover Vilva One product" },
        // { name: "Custom software", href: "/custom-software", ariaLabel: "Explore Custom software" },
        { name: "Verification API", href: "/verification-api", ariaLabel: "Learn Verification API" },
        { name: "Fintech", href: "/fintech", ariaLabel: "Explore Fintech" },
        // { name: "Growsuite", href: "/growsuite", ariaLabel: "Explore Growsuite product" },
        // { name: "Whatsease", href: "/whatsease", ariaLabel: "Explore Whatsease product" },  
        // { name: "Digital Marketing", href: "/digital-marketing", ariaLabel: "View digital marketing services" },
        // { name: "Pos", href: "/pos", ariaLabel: "Manage billing, inventory" },
        { name: "Digital Cart", href: "/digital-cart", ariaLabel: "Manage billing, inventory" },

      ],
    },
    {
      name: "Solutions",
      subItems: [
        {
          name: "Technology",
          subItems: [
            { name: "AI/ML", href: "/solutions/technology/ai-ml", ariaLabel: "Explore AI and Machine Learning solutions" },
            { name: "FinTech Solution", href: "/solutions/technology/fintech-solution", ariaLabel: "Learn about FinTech solutions" },
            { name: "Cyber Security", href: "/solutions/technology/cyber-security", ariaLabel: "View cyber security services" },
            { name: "KYC Verification", href: "/solutions/technology/kyc-verification", ariaLabel: "Discover KYC verification solutions" },
          ],
        },
        {
          name: "Marketing",
          subItems: [
            {
              name: "WhatsApp Marketing",
              subItems: [
                { name: "WhatsApp Business API", href: "/solutions/marketing/whatsapp-marketing/whatsapp-business-api", ariaLabel: "View WhatsApp Business API solutions" },
                { name: "Official WhatsApp", href: "/solutions/marketing/whatsapp-marketing/official-whatsapp-business-facebook", ariaLabel: "Discover WhatsApp Business integration with Facebook" },
              ],
            },
            {
              name: "Digital Marketing",
              subItems: [
                { name: "Facebook & Instagram Marketing", href: "/solutions/marketing/digital-marketing/facebook-instagram-marketing", ariaLabel: "Explore Facebook and Instagram marketing services" },
                { name: "LinkedIn Marketing", href: "/solutions/marketing/digital-marketing/linkedin-marketing", ariaLabel: "Learn about LinkedIn marketing services" },
                { name: "Google & YouTube Marketing", href: "/solutions/marketing/digital-marketing/google-youtube-marketing", ariaLabel: "Discover Google and YouTube marketing solutions" },
                { name: "Sales Conversion Marketing", href: "/solutions/marketing/digital-marketing/sales-conversion-marketing", ariaLabel: "Explore sales conversion marketing solutions" },
                { name: "Lead Generation Marketing", href: "/solutions/marketing/digital-marketing/lead-generation-marketing", ariaLabel: "Learn about lead generation marketing services" },
                { name: "Event Signup/Ticket Sales Marketing", href: "/solutions/marketing/digital-marketing/event-signup-ticket-sales", ariaLabel: "Discover event signup and ticket sales marketing" },
              ],
            },
            { name: "Email Marketing", href: "/solutions/marketing/email-marketing", ariaLabel: "View email marketing services" },
            { name: "SMS Marketing", href: "/solutions/marketing/sms-marketing", ariaLabel: "Explore SMS marketing solutions" },
            {
              name: "Voice Marketing",
              subItems: [
                { name: "Bulk Voice Calling", href: "/solutions/marketing/voice-marketing/bulk-voice-calling", ariaLabel: "Learn about bulk voice calling services" },
                { name: "Custom Voice Calling", href: "/solutions/marketing/voice-marketing/custom-voice-calling", ariaLabel: "Explore custom voice calling solutions" },
                { name: "API Based Voice Calling", href: "/solutions/marketing/voice-marketing/api-based-voice-calling", ariaLabel: "Discover API-based voice calling services" },
              ],
            },
            {
              name: "SEO & SMM",
              subItems: [
                { name: "Social Media Management & Marketing", href: "/solutions/marketing/seo-smm/social-media-management-marketing", ariaLabel: "View social media management and marketing services" },
              ],
            },
          ],
        },
        {
          name: "Ecommerce Solutions",
          subItems: [
            { name: "Magento website development", href: "/solutions/ecommerce-solutions/magento-website-development", ariaLabel: "Explore Magento website development services" },
            { name: "Opencart Website development", href: "/solutions/ecommerce-solutions/opencart-website-development", ariaLabel: "Learn about Opencart website development" },
            { name: "WordPress website development", href: "/solutions/ecommerce-solutions/wordpress-website-development", ariaLabel: "Discover WordPress website development services" },
            { name: "Shopify website development", href: "/solutions/ecommerce-solutions/shopify-website-development", ariaLabel: "View Shopify website development solutions" },
            { name: "Android App development", href: "/solutions/ecommerce-solutions/android-app-development", ariaLabel: "Explore Android app development services" },
            { name: "IOS App development", href: "/solutions/ecommerce-solutions/ios-app-development", ariaLabel: "Learn about iOS app development services" },
          ],
        },
        {
          name: "Industry",
          subItems: [
            {
              name: "Android Apps",
              subItems: [
                { name: "APP for Boutique", href: "/solutions/industry/android-apps/boutique", ariaLabel: "Discover Android app for boutiques" },
                { name: "APP for Restaurants", href: "/solutions/industry/android-apps/restaurants", ariaLabel: "Explore Android app for restaurants" },
                { name: "APP for Hotels", href: "/solutions/industry/android-apps/hotels", ariaLabel: "View Android app for hotels" },
                { name: "APP for Shops", href: "/solutions/industry/android-apps/retail-shops", ariaLabel: "Learn about Android app for shops" },
                { name: "APP for Ecommerce", href: "/solutions/industry/android-apps/ecommerce", ariaLabel: "Discover Android app for ecommerce" },
                { name: "APP for Groceries", href: "/solutions/industry/android-apps/grocery-store", ariaLabel: "Explore Android app for groceries" },
                { name: "APP for Fruits & Vegetables", href: "/solutions/industry/android-apps/fruits-and-vegetables", ariaLabel: "View Android app for fruits and vegetables" },
                { name: "APP for Wholesalers", href: "/solutions/industry/android-apps/wholesalers", ariaLabel: "Learn about Android app for wholesalers" },
              ],
            },
            {
              name: "Banking Solution",
              subItems: [
                { name: "Oracle pl/sql Web Services", href: "/solutions/industry/banking/oracle-plsql-web-services", ariaLabel: "Explore Oracle PL/SQL web services" },
                { name: "Payment Gateway Solutions", href: "/solutions/industry/banking-solution/payment-gateway-solutions", ariaLabel: "Learn about payment gateway solutions" },
                { name: "Payment Collection Services", href: "/solutions/industry/banking-solution/payment-collection-services", ariaLabel: "View payment collection services" },
                { name: "Bank Verification API's", href: "/solutions/industry/banking-solution/bank-verification-apis", ariaLabel: "Discover bank verification APIs" },
                { name: "Aadhar & PAN Verification API's", href: "/solutions/industry/banking-solution/aadhar-pan-verification-apis", ariaLabel: "Explore Aadhar and PAN verification APIs" },
                { name: "Virtual Accounts API", href: "/solutions/industry/banking/virtual-accounts-api", ariaLabel: "Learn about virtual accounts API" },
                { name: "Virtual Current Accounts", href: "/solutions/industry/banking-solution/virtual-current-accounts", ariaLabel: "Discover virtual current accounts" },
              ],
            },
          ],
        },
      ],
    },
    { name: "Partners", href: "/partners", ariaLabel: "View our partners" },
    { name: "Blog", href: "/blog", ariaLabel: "Read our blog" },
    {
      name: "More",
      subItems: [
        // /viewplan is the plan-comparison page — /chooseplan is website-plans-specific
        // (₹4,999 tiers) and would mislead a GrowSuite or VilvaChat visitor.
        { name: "Pricing", href: "/viewplan", ariaLabel: "Compare Vilva Business plans and pricing" },
        { name: "Career", href: "/careers", ariaLabel: "Explore career opportunities" },
        { name: "Our Journey", href: "/our-journey", ariaLabel: "Explore Our Journey of Vilva" },
        { name: "About Us", href: "/about-us", ariaLabel: "Learn about our company" },
        { name: "Our Founder", href: "/founder", ariaLabel: "Meet our founder, Gopinath Ravirajan" },
        { name: "Contact Us", href: "/contact-us", ariaLabel: "Contact our team" },
      ],
    },
  ];

  // Check if any submenu is open
  const isAnySubMenuOpen = Object.values(isSubMenuOpen).some((isOpen) => isOpen);

  const renderSubMenu = (items: any[], menuKey: string, level = 1) => (
    <>
      {/* Full-width background - only for level 1 */}
      {level === 1 && (
        <div
          className={`fixed left-0 right-0 top-[70px] w-full h-[300px] rounded-b-[24px] bg-white shadow-md z-40 ${isSubMenuOpen[menuKey] ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
            }`}
          onMouseEnter={() => handleMouseEnter(menuKey)}
          onMouseLeave={() => handleMouseLeave(menuKey)}
        >
          <div className="py-3"></div>
        </div>
      )}

      {/* Dropdown menu */}
      <div
        className={`${level === 1
          ? "absolute left-0 top-full mt-0 pt-[28px] w-[330px] z-50"
          : "absolute left-full top-0 ml-1 w-[300px] bg-white shadow-lg rounded-md z-50"
          } py-3 ${isSubMenuOpen[menuKey] ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
          }`}
        onMouseEnter={() => handleMouseEnter(menuKey, level > 1 ? menuKey.split(".").slice(0, -1).join(".") : undefined)}
        onMouseLeave={() => handleMouseLeave(menuKey)}
      >
        {items.map((item) => {
          const itemKey = `${menuKey}.${item.name}`;
          const isActive = item.href
            ? pathname === item.href
            : pathname.startsWith(`/${menuKey.toLowerCase().replace(/\./g, "/")}/${item.name.toLowerCase().replace(/\s+/g, "-")}`);
          return (
            <div
              key={item.name}
              className="relative"
              onMouseEnter={() => item.subItems && handleMouseEnter(itemKey, menuKey)}
              onMouseLeave={() => item.subItems && handleMouseLeave(itemKey)}
            >
              {item.href ? (
                <Link prefetch={false}
                  href={item.href}
                  className={`block px-4 py-2 schibsted-grotesk text-sm font-[500] ${isActive ? "text-[#195AFF]" : "text-[#30304B]"
                    } hover:text-[#195AFF] hover:bg-gray-50 transition-colors`}
                >
                  {item.name}
                </Link>
              ) : (
                <div
                  className={`flex justify-between items-center px-4 py-2 schibsted-grotesk text-sm font-[500] ${isActive ? "text-[#195AFF]" : "text-[#30304B]"
                    } hover:text-[#195AFF] hover:bg-gray-50 cursor-pointer transition-colors`}
                >
                  {item.name}
                  {item.subItems && (
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              )}
              {item.subItems && isSubMenuOpen[itemKey] && renderSubMenu(item.subItems, itemKey, level + 1)}
            </div>
          );
        })}
      </div>
    </>
  );

  const renderMobileSubMenu = (items: any[], menuKey: string, level = 1) => (
    <div className={`flex flex-col space-y-3 ${level > 1 ? "mt-2 ml-4" : "mt-4"}`}>
      {items.map((item) => {
        const itemKey = `${menuKey}.${item.name}`;
        const isActive = item.href
          ? pathname === item.href
          : pathname.startsWith(`/${menuKey.toLowerCase().replace(/\./g, "/")}/${item.name.toLowerCase().replace(/\s+/g, "-")}`);
        return (
          <div key={item.name} className="flex flex-col ">
            <div className="flex items-center justify-between ">
              {item.href ? (
                <Link prefetch={false}
                  href={item.href}
                  className={`text-[#090D28] text-[16px] leading-[116.369%] font-medium schibsted-grotesk not-italic ${isActive ? "text-[#195AFF]" : ""
                    }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ) : (
                <span
                  className={`text-[#090D28] text-[16px] leading-[116.369%] font-medium schibsted-grotesk not-italic ${isActive ? "text-[#195AFF]" : ""
                    }`}
                >
                  {item.name}
                </span>
              )}
              {item.subItems && (
                <button type="button" aria-label="Menu Button" onClick={() => toggleSubMenu(itemKey)} className="p-2">
                  <svg
                    className={`w-4 h-4 transform ${isSubMenuOpen[itemKey] ? "rotate-180" : ""}`}
                    fill="none"
                    stroke={isSubMenuOpen[itemKey] || isActive ? "#195AFF" : "#000"}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>
            {item.subItems && isSubMenuOpen[itemKey] && renderMobileSubMenu(item.subItems, itemKey, level + 1)}
          </div>
        );
      })}
    </div>
  );

  return (
    <nav className="bg-white sticky top-0 z-40">
      {/* Black Overlay - Shows when any dropdown is open */}
      {isAnySubMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 top-[70px]"
          style={{ pointerEvents: 'none' }}
        />
      )}

      <div className="px-[24px] mx-auto">
        <div className="flex justify-between items-center py-3 px-2 md:px-0">
          <div className="flex items-center gap-12">
            <Link prefetch={false} href="/">
              <div className="flex items-center">
                {/* Mobile logo */}
                <img
                  src={vilvaLogo.src}
                  alt="Vilva Logo"
                  loading="eager"
                  fetchPriority="high"
                  width={164}
                  height={59}
                  className="block md:hidden w-[100px] h-auto"
                />

                {/* Desktop logo — LCP element, load eagerly */}
                <img
                  src={vilvaLogo.src}
                  alt="Vilva Logo"
                  loading="eager"
                  fetchPriority="high"
                  width={164}
                  height={59}
                  className="hidden md:block w-[163px] h-auto"
                />
              </div>

            </Link>
            <div className="hidden md:flex items-center md:space-x-4 lg:space-x-8">
              {navItems.map((item) => {
                const isParentActive = item.subItems
                  ? pathname.startsWith(`/${item.name.toLowerCase()}`)
                  : pathname === item.href;
                return (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => item.subItems && handleMouseEnter(item.name)}
                    onMouseLeave={() => item.subItems && handleMouseLeave(item.name)}
                  >
                    <div className="flex items-center">
                      <Link prefetch={false}
                        href={item.href || "#"}
                        aria-label={item.ariaLabel || item.name}
                        className={`text-[#000] text-[16px] leading-[116.369%] font-[500] schibsted-grotesk not-italic ${isParentActive ? "text-[#195AFF]" : "text-[#30304B]"
                          } hover:text-[#195AFF]`}
                      >
                        {item.name}
                      </Link>
                      {item.subItems && <SvgIcons.NavbarArrow />}
                    </div>
                    {item.subItems && isSubMenuOpen[item.name] && renderSubMenu(item.subItems, item.name)}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="md:hidden flex items-center">
            <button
              type="button"
              aria-label="Menu Button"
              onClick={() => setIsOpen(!isOpen)}
              className="text-[#090D28] focus:outline-none bg-[#EFF4FF] rounded-[8px] p-[6px]"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
          <div className="hidden md:flex space-x-3">
            {isLoggedIn ? (
              <div className="relative group" onMouseEnter={handleProfileHover} onMouseLeave={handleDropdownLeave}>
                <div
                  className={`transition-border duration-900 ease-in-out ${isProfileOpen ? "border-[2px] border-[#195AFF] rounded-full" : "border-2 border-transparent"
                    }`}
                >
                  <img
                    src={getImageUrl(`/vilvabusiness/customers/profile/${data?.id}/${data?.profile_pic}`)}
                    alt="Profile"
                    className="border-1 rounded-full w-11 h-11 object-cover"
                    onError={(e) => {
                      (e.currentTarget.style.display = "none");
                      e.currentTarget.insertAdjacentHTML(
                        "afterend",
                        `<div class="border-1 rounded-full w-11 h-11 bg-gray-400 flex items-center justify-center text-xl font-medium text-white">
                          ${data?.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>`
                      );
                    }}
                  />
                </div>
                <div
                  className={`absolute w-[399px] right-3 top-8 flex-shrink-0 rounded-[8px] bg-white shadow-[-178px_277px_92px_0_rgba(0,0,0,0),-114px_177px_84px_0_rgba(0,0,0,0.01),-64px_100px_71px_0_rgba(0,0,0,0.05),-29px_44px_53px_0_rgba(0,0,0,0.09),-7px_11px_29px_0_rgba(0,0,0,0.10)] transition-all duration-900 ease-in-out z-10 ${isProfileOpen ? "block" : "hidden"
                    }`}
                >
                  <div className="flex relative z-10 items-center gap-[12px] p-[16px]">
                    <img
                      src={getImageUrl(`/vilvabusiness/customers/profile/${data?.id}/${data?.profile_pic}`)}
                      alt="Profile"
                      className="border-1 rounded-full w-11 h-11 object-cover"
                      onError={(e) => {
                        (e.currentTarget.style.display = "none");
                        e.currentTarget.insertAdjacentHTML(
                          "afterend",
                          `<div class="border-1 rounded-full w-11 h-11 bg-gray-400 flex items-center justify-center text-xl font-medium text-white">
                            ${data?.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>`
                        );
                      }}
                    />
                    <div>
                      <p className="text-[#00021F] m-0 schibsted-grotesk first-letter:uppercase text-[18px] font-[400] leading-tight">
                        {data?.name?.split(" ")[0] || "Guest"}
                      </p>
                      <Link prefetch={false}
                        href="/portal"
                        className="text-[#195AFF] font-LatinkaRegular text-[14px] font-normal leading-tight mb-[8px]"
                      >
                        Open Dashboard
                      </Link>
                    </div>
                  </div>
                  <div className="p-[16px] pt-[0px] flex gap-[8px] justify-center items-center">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="text-[#EF1717] w-full schibsted-grotesk text-[15px] font-normal capitalize flex px-4 py-2 justify-center items-center gap-2 self-stretch rounded-[8px] border-[0.6px] h-[40px] border-[#F5B2B2] bg-[#FFEAEA] shadow-[0_0_4px_0_rgba(0,0,0,0.05)]"
                    >
                      {loader ? (
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-[#EF1717]"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : (
                        <SvgIcons.logout />
                      )}
                      {loader ? "" : "Logout"}
                    </button>
                    <Link prefetch={false}
                      href="/portal/myaccount"
                      className="text-white w-full schibsted-grotesk border-1 border-[#195AFF] text-[16px] whitespace-nowrap font-normal leading-[107.692%] flex px-4 py-[16px] h-[40px] justify-center items-center gap-1 rounded-[8px] bg-[#195AFF]"
                    >
                      My Profile
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Link prefetch={false}
                  href="/signin"
                  className="inline-flex h-11 md:px-4 lg:px-6 py-4 text-[#195AFF] justify-center items-center shrink-0 rounded-[12px] border border-[#6F9CFF] bg-[#EFF4FF] schibsted-grotesk md:text-[16px] lg:text-base font-[500] leading-[120%]"
                >
                  Login
                </Link>
                <Link prefetch={false}
                  href="/signup"
                  className="inline-flex h-11 md:px-4 lg:px-6 py-4 justify-center items-center shrink-0 rounded-[12px] bg-[#195AFF] text-white schibsted-grotesk md:text-[16px] lg:text-base font-[500] leading-[120%]"
                >
                  Signup
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 top-[71px] z-10 md:hidden"
            onClick={() => setIsOpen(false)}
          />
          <div className="md:hidden w-[85%] ms-auto z-[10000] fixed top-[68px] right-0 h-full overflow-y-auto">
            <div className="flex flex-col px-4 py-6 space-y-4 bg-white h-fit">
              {isLoggedIn && (
                <>
                  <div className="flex items-center gap-[12px]">
                      <img
                      src={getImageUrl(`/vilvabusiness/customers/profile/${data?.id}/${data?.profile_pic}`)}
                      alt="Profile"
                      className="border-1 rounded-full w-11 h-11 object-cover"
                      onError={(e) => {
                        (e.currentTarget.style.display = "none");
                        e.currentTarget.insertAdjacentHTML(
                          "afterend",
                          `<div class="border-1 rounded-full w-11 h-11 bg-gray-400 flex items-center justify-center text-xl font-medium text-white">
                            ${data?.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>`
                        );
                      }}
                    />
                    <div>
                      <p className="text-[#00021F] m-0 schibsted-grotesk first-letter:uppercase text-[18px] font-[400] leading-tight">
                        {user?.username?.split(" ")[0] || "Guest"}
                      </p>
                      <Link prefetch={false}
                        href="/portal"
                        className="text-[#195AFF] font-LatinkaRegular text-[14px] font-normal leading-tight mb-[8px]"
                        onClick={() => setIsOpen(false)}
                      >
                        Open Dashboard
                      </Link>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-[8px]">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="text-[#EF1717] h-[40px] w-[50%] schibsted-grotesk text-[15px] font-normal capitalize flex px-4 py-2 justify-center items-center gap-2 self-stretch rounded-[8px] border-[0.6px] border-[#F5B2B2] bg-[#FFEAEA] shadow-[0_0_4px_0_rgba(0,0,0,0.05)]"
                    >
                      {loader ? (
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-[#EF1717]"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : (
                        <SvgIcons.logout />
                      )}
                      {loader ? "" : "Logout"}
                    </button>
                    <Link prefetch={false}
                      href="/portal/myaccount"
                      className="inline-flex h-[40px] w-[50%] px-6 py-4 justify-center items-center shrink-0 rounded-[12px] bg-[#195AFF] text-white schibsted-grotesk text-base font-semibold leading-[120%]"
                      onClick={() => setIsOpen(false)}
                    >
                      My Profile
                    </Link>
                  </div>
                </>
              )}
              {navItems.map((item) => {
                const isParentActive = item.subItems
                  ? pathname.startsWith(`/${item.name.toLowerCase()}`)
                  : pathname === item.href;
                return (
                  <div key={item.name} className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <Link prefetch={false}
                        href={item.href || "#"}
                        aria-label={item.ariaLabel || item.name}
                        className={`schibsted-grotesk text-[16px] not-italic leading-[18.619px] ${isParentActive ? "text-[#195AFF]" : "text-[#30304B]"
                          }`}
                        onClick={() => !item.subItems && setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                      {item.subItems && (
                        <button type="button" aria-label="Menu Button" onClick={() => toggleSubMenu(item.name)} className="p-2">
                          <svg
                            className={`w-4 h-4 transform ${isSubMenuOpen[item.name] ? "rotate-180" : ""}`}
                            fill="none"
                            stroke={isSubMenuOpen[item.name] || isParentActive ? "#195AFF" : "#000"}
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {item.subItems && isSubMenuOpen[item.name] && renderMobileSubMenu(item.subItems, item.name)}
                  </div>
                );
              })}
              {!isLoggedIn && (
                <>
                  <Link prefetch={false}
                    href="/signup"
                    className="inline-flex h-11 mt-6 mb-2 px-6 py-4 justify-center items-center shrink-0 rounded-[12px] bg-[#195AFF] text-white schibsted-grotesk text-base font-semibold leading-[120%]"
                    onClick={() => setIsOpen(false)}
                  >
                    Sign Up
                  </Link>
                  <Link prefetch={false}
                    href="/signin"
                    className="inline-flex h-11 px-6 py-4 text-[#090D28] justify-center items-center shrink-0 rounded-[12px] bg-[#EFF4FF] schibsted-grotesk text-base font-semibold leading-[120%]"
                    onClick={() => setIsOpen(false)}
                  >
                    Log In
                  </Link>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </nav>
  );
}
